import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import { BaseEntity } from './base.entity';
import { ClientAddressItemEntity } from './client-address-item.entity';
import { ClientAddressEntity } from './client-address.entity';
import { ClientItemEntity } from './client-item.entity';
import { ClientEntity } from './client.entity';
import { CompanyEntity } from './company.entity';
import { InvoiceDetailEntity } from './invoice-detail.entity';
import { InvoiceFileEntity } from './invoice-file.entity';
import { MSmPaymentTransactionInvoiceEntity } from './m-sm-payment-transaction-invoice.entity';
import { TaxEntity } from './tax.entity';
import { TermsCodeEntity } from './terms-code.entity';

@Index('externalId_fk_company__id', ['externalId', 'fkCompanyId'], {
	unique: true,
})
@Index('fk_company__id', ['fkCompanyId'], {})
@Index('fk_tax__id', ['fkTaxId'], {})
@Index('invoice__deleted_userId_index', ['deletedUserId'], {})
@Index(
	'invoice__fk_client__id_date_type__deleted_userId_index',
	['fkClientId', 'date', 'type', 'deletedUserId'],
	{},
)
@Index('invoice__fk_client__id_fk', ['fkClientId'], {})
@Index(
	'invoice__fk_client_address__id_billing_fk',
	['fkClientAddressIdBilling'],
	{},
)
@Index('invoice_date__deleted_userId_index', ['date', 'deletedUserId'], {})
@Index('invoice_date_index', ['date'], {})
@Entity('invoice')
export class InvoiceEntity extends BaseEntity {
	@Column('varchar', { name: 'externalId', nullable: true, length: 64 })
	public externalId!: string | null;

	@Column('date', { name: 'date', nullable: true })
	public date!: string | null;

	@Column('int', { name: 'numberOfLines', nullable: true })
	public numberOfLines!: number | null;

	@Column('decimal', {
		name: 'subtotal',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public subtotal!: string | null;

	@Column('decimal', {
		name: 'freight',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public freight!: string | null;

	@Column('decimal', {
		name: 'discount',
		nullable: true,
		precision: 21,
		scale: 10,
	})
	public discount!: string | null;

	@Column('decimal', { name: 'total', nullable: true, precision: 13, scale: 2 })
	public total!: string | null;

	/**
	 * PD-15059 — the payment processing fee stamp, copied from the source
	 * order (same semantics: the CONFIGURED value — "3" with unit '%' or a
	 * flat amount with '$'). The invoice total INCLUDES the computed fee so
	 * it ties exactly to what the customer's payment method is charged; the
	 * fee's dollar value is derivable as
	 * `total − (subtotal + freight + tax − discount)`, which is also the
	 * pre-fee base a gateway charge should be requested for (the payments
	 * layer re-applies the fee at charge time).
	 */
	@Column('varchar', {
		name: 'm_sm_payment_fee_unit',
		nullable: true,
		length: 32,
	})
	public mSmPaymentFeeUnit!: string | null;

	@Column('decimal', {
		name: 'm_sm_payment_fee_amount',
		precision: 13,
		scale: 2,
		default: () => "'0.00'",
	})
	public mSmPaymentFeeAmount!: string;

	@Column('decimal', {
		name: 'totalGP',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public totalGp!: string | null;

	@Column('datetime', { name: 'lastImportDate', nullable: true })
	public lastImportDate!: Date | null;

	@Column('varchar', { name: 'type', nullable: true, length: 10 })
	public type!: string | null;

	/**
	 * PD-15026 — why a credit was issued, from the fixed `CREDIT_MEMO_REASONS`
	 * vocabulary. Populated only on credit memos (`type = InvoiceType.CREDIT_MEMO`).
	 *
	 * A real column rather than a note or a custom field because credits have
	 * to be reportable in aggregate — "how much are we crediting, for what, and
	 * to whom" is the control on a process that hands money back. A fixed
	 * vocabulary in its own column groups; free text in a JSON blob does not.
	 */
	@Column('varchar', { name: 'creditMemoReason', nullable: true, length: 64 })
	public creditMemoReason!: string | null;

	/** PD-15026 — free-text detail on the credit, e.g. which delivery it came from. */
	@Column('varchar', { name: 'creditMemoNote', nullable: true, length: 255 })
	public creditMemoNote!: string | null;

	@Column('varchar', { name: 'status', nullable: true, length: 32 })
	public status!: string | null;

	@Column('decimal', { name: 'tax', nullable: true, precision: 13, scale: 2 })
	public tax!: string | null;

	@Column('varchar', { name: 'externalId2', nullable: true, length: 255 })
	public externalId2!: string | null;

	@Column('int', { name: '_fk_client__id', unsigned: true })
	public fkClientId!: number;

	@Column('int', {
		name: '_fk_client_address__id_billing',
		nullable: true,
		unsigned: true,
	})
	public fkClientAddressIdBilling!: number | null;

	@Column('int', {
		name: 'fk_company__id',
		unsigned: true,
		default: () => "'1'",
	})
	public fkCompanyId!: number;

	@Column('decimal', {
		name: 'totalGPMargin',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public totalGpMargin!: string | null;

	@Column('decimal', {
		name: 'm_sm_payment_outstandingBalance',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public mSmPaymentOutstandingBalance!: string | null;

	@Column('decimal', {
		name: 'shipCost',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public shipCost!: string | null;

	@Column('varchar', { name: 'paymentTerm', nullable: true, length: 255 })
	public paymentTerm!: string | null;

	@Column('int', {
		name: 'fk_terms_code__id',
		nullable: true,
		unsigned: true,
	})
	public fkTermsCodeId!: number | null;

	@ManyToOne(() => TermsCodeEntity, (termsCode) => termsCode.invoices, {
		onDelete: 'SET NULL',
		onUpdate: 'CASCADE',
	})
	@JoinColumn([{ name: 'fk_terms_code__id', referencedColumnName: 'id' }])
	public fkTermsCode!: TermsCodeEntity | null;

	@Column('decimal', {
		name: 'earlyPayDiscountPercentSnapshot',
		nullable: true,
		precision: 5,
		scale: 2,
	})
	public earlyPayDiscountPercentSnapshot!: string | null;

	@Column('int', {
		name: 'earlyPayDiscountWindowDaysSnapshot',
		nullable: true,
	})
	public earlyPayDiscountWindowDaysSnapshot!: number | null;

	@Column('date', { name: 'dueDate', nullable: true })
	public dueDate!: string | null;

	@Column('varchar', { name: 'discountType', length: 32, default: () => "'$'" })
	public discountType!: string;

	@Column('int', { name: 'fk_tax__id', nullable: true, unsigned: true })
	public fkTaxId!: number | null;

	@Column('decimal', {
		name: 'taxRate',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public taxRate!: string | null;

	@Column('json', {
		name: 'customFields',
		nullable: true,
		comment: 'JSON key-value pairs for tenant-specific custom fields.',
	})
	public customFields!: Record<string, unknown> | null;

	@OneToMany(() => ClientEntity, (client) => client.fkInvoiceIdLast2, {})
	public clients!: Array<ClientEntity>;

	@OneToMany(() => ClientEntity, (client) => client.fkInvoice, {})
	public clients2!: Array<ClientEntity>;

	@OneToMany(
		() => ClientAddressItemEntity,
		(clientAddressItem) => clientAddressItem.fkInvoiceIdLast2,
		{},
	)
	public clientAddressItems!: Array<ClientAddressItemEntity>;

	@OneToMany(
		() => ClientItemEntity,
		(clientItem) => clientItem.fkInvoiceIdLast2,
		{},
	)
	public clientItems!: Array<ClientItemEntity>;

	@OneToMany(() => ClientItemEntity, (clientItem) => clientItem.fkInvoice, {})
	public clientItems2!: Array<ClientItemEntity>;

	@ManyToOne(() => CompanyEntity, (company) => company.invoices, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_company__id', referencedColumnName: 'id' }])
	public fkCompany!: CompanyEntity;

	@ManyToOne(() => TaxEntity, (tax) => tax.invoices, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([{ name: 'fk_tax__id', referencedColumnName: 'id' }])
	public fkTax!: TaxEntity;

	@ManyToOne(
		() => ClientAddressEntity,
		(clientAddress) => clientAddress.invoices,
		{
			onDelete: 'RESTRICT',
			onUpdate: 'RESTRICT',
		},
	)
	@JoinColumn([
		{ name: '_fk_client_address__id_billing', referencedColumnName: 'id' },
	])
	public fkClientAddressIdBilling2!: ClientAddressEntity;

	@ManyToOne(() => ClientEntity, (client) => client.invoices, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: '_fk_client__id', referencedColumnName: 'id' }])
	public fkClient!: ClientEntity;

	@OneToMany(
		() => InvoiceFileEntity,
		(invoiceFile) => invoiceFile.fkInvoice,
		{},
	)
	public invoiceFiles!: Array<InvoiceFileEntity>;

	@OneToMany(
		() => InvoiceDetailEntity,
		(invoiceDetail) => invoiceDetail.invoice,
		{},
	)
	public invoiceDetails!: Array<InvoiceDetailEntity>;

	@OneToMany(
		() => MSmPaymentTransactionInvoiceEntity,
		(mSmPaymentTransactionInvoice) =>
			mSmPaymentTransactionInvoice.mSmPaymentFkInvoice,
		{},
	)
	public mSmPaymentTransactionInvoices!: Array<MSmPaymentTransactionInvoiceEntity>;
}
